import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { initTelegramApp, isTelegramWebApp } from "./lib/telegram";
import { LocaleProvider, useLocale } from "./lib/i18n";
import { useCurrentUser } from "./hooks/use-current-user";
import { SplashPage } from "./pages/SplashPage";
import { RoleChooserPage } from "./pages/RoleChooserPage";
import { HomePage } from "./pages/HomePage";
import { TelegramRequiredPage } from "./pages/TelegramRequiredPage";
import { RequestPage } from "./pages/RequestPage";
import { RequestDetailPage } from "./pages/RequestDetailPage";
import { ProviderDetailPage } from "./pages/ProviderDetailPage";
import { HandymanProfilePage } from "./pages/HandymanProfilePage";
import { HandymanJobDetailPage } from "./pages/HandymanJobDetailPage";
import { HandymanPortfolioPage } from "./pages/HandymanPortfolioPage";
import { HandymanVerificationPage } from "./pages/HandymanVerificationPage";
import { HandymenPage } from "./pages/HandymenPage";
import { Role } from "@mestergo/shared";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

function AppRoutes() {
  const { user, isLoading, error } = useCurrentUser();
  const { t } = useLocale();
  const location = useLocation();

  if (!isTelegramWebApp()) {
    return (
      <TelegramRequiredPage
        title={t.openInTelegramTitle}
        message={t.openInTelegramMessage}
      />
    );
  }

  if (isLoading && !user) return <SplashPage />;
  if (error) {
    return (
      <TelegramRequiredPage
        title={t.sessionNeededTitle}
        message={t.sessionNeededMessage}
      />
    );
  }

  const hasRole = user && user.roles.length > 0;
  const hasHandymanRole = user?.roles.some((r) => r === Role.Handyman);
  const isHandymanPath = location.pathname.startsWith("/handyman");

  if (!hasRole || (isHandymanPath && !hasHandymanRole)) return <RoleChooserPage />;

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/request" element={<RequestPage />} />
      <Route path="/requests/:id" element={<RequestDetailPage />} />
      <Route path="/handymen/:id" element={<ProviderDetailPage />} />
      <Route path="/handyman/profile" element={<HandymanProfilePage />} />
      <Route path="/handyman/portfolio" element={<HandymanPortfolioPage />} />
      <Route path="/handyman/verification" element={<HandymanVerificationPage />} />
      <Route path="/handyman/jobs/:requestId" element={<HandymanJobDetailPage />} />
      <Route path="/handymen" element={<HandymenPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  useEffect(() => {
    initTelegramApp();
  }, []);

  return (
    <LocaleProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </QueryClientProvider>
    </LocaleProvider>
  );
}
